/**
 * Unified Cancel Session Flow
 * 
 * CRITICAL: This utility ensures ALL cancel/back paths properly release the connector
 * by calling POST /api/sessions/:sessionUid/cancel BEFORE clearing local state.
 * 
 * Problem: Backend logs showed "authorized" sessions blocking connectors even after user
 * clicked Cancel/Back, indicating:
 * - Frontend never called cancel endpoint, OR
 * - Frontend cleared sessionUid before calling cancel, OR
 * - Cancel call failed and we didn't retry
 * 
 * Solution: Single source of truth for cancellation with:
 * - Proper logging (correlationId, sessionUid, reason)
 * - Retry logic (one retry after 500ms for network flakes)
 * - Never clear sessionUid before cancel attempt
 * - Handle all response types (ok, 404, error)
 * - Idempotent (safe to call multiple times)
 */

import { cancelSession as apiCancelSession, getCustomerSession } from '../api/sessionsApi.js';

/**
 * Generate correlation ID for cancel tracking
 */
function getCancelCorrelationId() {
  return `cancel-${Date.now()}-${Math.random().toString(36).substring(7)}`;
}

/**
 * Unified cancel and cleanup function
 * 
 * This is the ONLY function that should be used to cancel sessions.
 * All cancel/back/exit paths should route through this function.
 * 
 * @param {object} params - Cancel parameters
 * @param {string} params.sessionUid - Session UID to cancel (REQUIRED)
 * @param {string} params.reason - Human-readable cancel reason for logging
 * @param {string} params.source - Component/screen triggering cancel
 * @param {object} params.context - Additional context (stationId, connectorId, etc.)
 * @param {function} params.onSuccess - Callback after successful cancel
 * @param {function} params.onError - Callback after failed cancel
 * 
 * @returns {Promise<object>} { success, released, message, correlationId }
 */
export async function cancelAndCleanup({
  sessionUid,
  reason = 'user_cancel',
  source = 'unknown',
  context = {},
  onSuccess = null,
  onError = null
}) {
  const correlationId = getCancelCorrelationId();
  
  // ============================================================
  // VALIDATION: Must have sessionUid
  // ============================================================
  if (!sessionUid || typeof sessionUid !== 'string') {
    const error = {
      success: false,
      error: 'No sessionUid to cancel',
      reason: 'missing_session_uid',
      correlationId
    };
    
    console.warn('[CANCEL_FLOW] ⚠️  No sessionUid - skipping cancel', {
      source,
      reason,
      correlationId
    });
    
    if (onError) onError(error);
    return error;
  }
  
  // ============================================================
  // LOG: Cancel attempt
  // ============================================================
  console.log('[CANCEL_FLOW] 🚫 trigger', {
    source,
    reason,
    sessionUid,
    stationId: context.stationId,
    connectorId: context.connectorId,
    correlationId,
    timestamp: new Date().toISOString()
  });

  // ============================================================
  // DIAGNOSTIC: SESSION_CANCEL_TRACE
  // Every path that hits cancelAndCleanup flows through here. The `source`
  // and `reason` fields are caller-supplied; stack trace captures the
  // actual JS call chain so we can pin down which screen / effect / event
  // fired the cancel during the reported regression.
  // ============================================================
  console.log('[SESSION_CANCEL_TRACE]', {
    site: 'cancelAndCleanup',
    source,
    reason,
    sessionUid,
    stationId: context.stationId,
    connectorId: context.connectorId,
    liveStatus: context.liveStatus ?? null,
    txId: context.txId ?? null,
    hasReceivedMeterValues: context.hasReceivedMeterValues ?? null,
    energyKWh: context.energyKWh ?? null,
    paymentStatus: context.paymentStatus ?? null,
    step: context.step ?? null,
    backendStatus: context.backendStatus ?? null,
    correlationId,
    ts: new Date().toISOString(),
    trace: (new Error().stack || '').split('\n').slice(2, 6).join(' | ')
  });
  
  // ============================================================
  // ATTEMPT 1: Call cancel API
  // ============================================================
  let result = await callCancelApi(sessionUid, correlationId);
  
  // ============================================================
  // RETRY: If failed (non-404), retry once after 500ms
  // ============================================================
  if (!result.ok && !result.notFound) {
    console.warn('[CANCEL_FLOW] ⚠️  First attempt failed, retrying after 500ms...', {
      error: result.error,
      correlationId
    });
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    result = await callCancelApi(sessionUid, `${correlationId}-retry`);
  }
  
  // ============================================================
  // RESPONSE HANDLING
  // ============================================================
  
  // SUCCESS: API returned ok:true or released:true or stopping:true
  if (result.ok || result.released || result.stopping) {
    console.log('[CANCEL_FLOW] ✅ cancel_result', {
      ok: result.ok,
      released: result.released,
      stopping: result.stopping,
      status: result.status,
      sessionUid,
      correlationId
    });
    
    const response = {
      success: true,
      released: result.released || result.stopping || true,
      message: 'Session cancelled successfully',
      correlationId: result.correlationId || correlationId
    };
    
    if (onSuccess) onSuccess(response);
    return response;
  }
  
  // 404: Session not found (already cancelled/expired/voided)
  if (result.notFound) {
    console.log('[CANCEL_FLOW] ℹ️  cancel_result', {
      ok: false,
      notFound: true,
      status: 404,
      sessionUid,
      correlationId,
      message: 'Session not found (already cancelled/expired)'
    });
    
    const response = {
      success: true, // Treat as success (idempotent)
      released: true,
      message: 'Session already cancelled or expired',
      idempotent: true,
      correlationId: result.correlationId || correlationId
    };
    
    if (onSuccess) onSuccess(response);
    return response;
  }
  
  // ERROR: API call failed
  console.error('[CANCEL_FLOW] ❌ cancel_result', {
    ok: false,
    error: result.error,
    message: result.message,
    status: result.status,
    sessionUid,
    correlationId
  });
  
  const errorResponse = {
    success: false,
    released: false,
    error: result.error || 'CANCEL_FAILED',
    message: result.message || 'Failed to cancel session',
    correlationId: result.correlationId || correlationId
  };
  
  if (onError) onError(errorResponse);
  return errorResponse;
}

/**
 * Call cancel API with error handling
 * Internal helper - do not call directly
 */
async function callCancelApi(sessionUid, correlationId) {
  try {
    const result = await apiCancelSession(sessionUid);
    return result;
  } catch (error) {
    console.error('[CANCEL_FLOW] 💥 Exception during cancel API call:', {
      error: error.message,
      stack: error.stack,
      correlationId
    });
    
    return {
      ok: false,
      error: 'NETWORK_ERROR',
      message: error.message || 'Network error during cancel',
      correlationId
    };
  }
}

/**
 * Cancel a blocking session that's preventing new session creation
 * Used when createSession fails with CONNECTOR_BUSY and returns blocking.session_uid
 * 
 * @param {string} blockingSessionUid - Session UID of the blocking session
 * @param {object} context - Context info (stationId, connectorId)
 * @returns {Promise<object>} Cancel result
 */
export async function clearBlockingSession(blockingSessionUid, context = {}) {
  if (!blockingSessionUid) {
    return {
      success: false,
      error: 'No blocking session UID provided'
    };
  }
  
  console.log('[CANCEL_FLOW] 🔓 Clearing blocking session:', {
    blockingSessionUid,
    stationId: context.stationId,
    connectorId: context.connectorId
  });

  // DIAGNOSTIC: triggered only by user clicking "Clear previous attempt"
  // on the CONNECTOR_BUSY UI. The cancelled sessionUid here is the
  // backend-reported BLOCKING one, not the user's current intended UID.
  console.log('[SESSION_CANCEL_TRACE]', {
    site: 'clearBlockingSession',
    blockingSessionUid,
    stationId: context.stationId ?? null,
    connectorId: context.connectorId ?? null,
    ts: new Date().toISOString(),
    note: 'user-initiated via CONNECTOR_BUSY clear button'
  });

  return cancelAndCleanup({
    sessionUid: blockingSessionUid,
    reason: 'clear_blocking_session',
    source: 'connector_busy_handler',
    context
  });
}

/**
 * Discard a cancel marker left by an older bundle.
 *
 * Older bundles beaconed a cancel on `beforeunload` and stored the session in
 * `ev:pendingCancelSession` so the NEXT page load would cancel it again —
 * without asking the server what state it was in. That load is usually just
 * a reload of a valid attempt (see App.jsx). The marker is now removed and
 * never acted on; the first load after this bundle ships must not cancel the
 * session the driver is reloading into.
 */
export async function cleanupPendingCancelSessions() {
  try {
    if (localStorage.getItem('ev:pendingCancelSession') === null) return;
    localStorage.removeItem('ev:pendingCancelSession');
    console.log('[CANCEL_FLOW] Discarded a legacy pending-cancel marker (not executed)');
  } catch {
    // storage unavailable — nothing to discard
  }
}

/**
 * Cancel a session on the server ONLY if it is still a pre-charge attempt.
 *
 * For an explicit "I'm abandoning this" action (e.g. "Start over"). Wiping
 * local state alone leaves the server holding the connector for 10-15 minutes
 * against this very driver (production 2026-09-24, GCEV3113569).
 *
 * The server's own state decides, not local state: anything started, with a
 * transaction, or past pending/authorized is left alone, because the cancel
 * route can issue a RemoteStop for a session that has begun. Best-effort: a
 * failure here never blocks the user, and the server reclaims an attempt its
 * own driver comes back for anyway.
 *
 * @returns {Promise<{cancelled: boolean, reason: string}>}
 */
export async function cancelIfStillPreCharge(sessionUid) {
  if (!sessionUid) return { cancelled: false, reason: 'no_session' };
  try {
    const current = await getCustomerSession(sessionUid);
    const s = current?.ok ? current.session : null;
    if (!s) return { cancelled: false, reason: 'unknown_state' };
    const preCharge = ['pending', 'authorized'].includes(s.status) && !s.started_at && !s.tx_id;
    if (!preCharge) return { cancelled: false, reason: `not_pre_charge:${s.status}` };
    const result = await apiCancelSession(sessionUid);
    return { cancelled: Boolean(result?.ok), reason: result?.ok ? 'cancelled' : (result?.error || 'cancel_failed') };
  } catch (err) {
    return { cancelled: false, reason: err?.message || 'error' };
  }
}
